import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { GoogleIcon } from '../components/icons';

interface ClientAuthScreenProps {
    onBack: () => void;
    onForgotPassword: () => void;
    onSwitchToPro: () => void;
}

const ClientAuthScreen: React.FC<ClientAuthScreenProps> = ({ onBack, onForgotPassword, onSwitchToPro }) => {
    const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');

    // Shared Form State
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState(''); // Only for register

    // UI State
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [showRoleMismatchModal, setShowRoleMismatchModal] = useState(false);
    const [mismatchRole, setMismatchRole] = useState<'restaurateur' | 'livreur' | null>(null);
    const [showIncorrectPasswordPopup, setShowIncorrectPasswordPopup] = useState(false);
    const [showExistingUserPopup, setShowExistingUserPopup] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    const { login, register, loginWithGoogle, devCreateTestAccount, pendingRoleMismatch, clearRoleMismatch } = useAuth();
    const { setView } = useContext(AppContext);

    useEffect(() => {
        let timer: number;
        if (cooldown > 0) {
            timer = window.setInterval(() => setCooldown(prev => prev - 1), 1000);
        }
        return () => clearInterval(timer);
    }, [cooldown]);
    useEffect(() => {
        if (pendingRoleMismatch) {
            setError(pendingRoleMismatch.message + " Souhaitez-vous rejoindre l'espace Pro ?");
            setMismatchRole(pendingRoleMismatch.role as any);
            setShowRoleMismatchModal(true);
            clearRoleMismatch();
        }
    }, [pendingRoleMismatch, clearRoleMismatch]);

    // Clean errors when switching tabs
    const handleTabSwitch = (tab: 'login' | 'register') => {
        setActiveTab(tab);
        setError('');
        setShowErrorModal(false);
    };

    // --- Handlers ---
    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        const cleanEmail = email.replace(/\s/g, '').trim();
        if (!cleanEmail || !password) {
            setError("Veuillez remplir tous les champs.");
            setShowErrorModal(true);
            setIsLoading(false);
            return;
        }

        try {
            await login(cleanEmail, password, 'client');
            setView({ screen: 'home' });
        } catch (err: any) {
            const errorMsg = (err?.message || String(err)).toLowerCase();

            if (errorMsg.includes('abort') || errorMsg.includes('cancelled')) return;

            if (errorMsg.includes('not_registered')) {
                setError("Ce compte n'est pas encore inscrit. Veuillez passer à l'inscription.");
                setShowErrorModal(true);
            } else if (errorMsg.includes('wrong_role_login:restaurateur')) {
                setError("Ces identifiants appartiennent à un profil Restaurant. Souhaitez-vous rejoindre l'espace Pro ?");
                setMismatchRole('restaurateur');
                setShowRoleMismatchModal(true);
            } else if (errorMsg.includes('wrong_role_login:livreur')) {
                setError("Ces identifiants appartiennent à un profil Livreur. Souhaitez-vous rejoindre l'espace Pro ?");
                setMismatchRole('livreur');
                setShowRoleMismatchModal(true);
            } else {
                let displayMsg = "Identifiants incorrects ou erreur de connexion.";
                if (errorMsg.includes('email not confirmed')) {
                    displayMsg = "Veuillez confirmer votre email avant de vous connecter.";
                    setError(displayMsg);
                    setShowErrorModal(true);
                } else if (
                    errorMsg.includes('invalid login credentials') ||
                    errorMsg.includes('invalid password') ||
                    errorMsg.includes('login failed') ||
                    errorMsg.includes('invalid')
                ) {
                    setShowIncorrectPasswordPopup(true);
                } else {
                    setError(displayMsg + " (" + errorMsg + ")");
                    setShowErrorModal(true);
                }
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        if (cooldown > 0) return;

        if (password.length < 6) {
            setError("Le mot de passe doit contenir au moins 6 caractères.");
            setShowErrorModal(true);
            return;
        }

        const cleanEmail = email.replace(/\s/g, '').trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(cleanEmail)) {
            setError("L'adresse email est invalide.");
            setShowErrorModal(true);
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            // Role is fixed to 'client' for this screen
            const result = await register(name, cleanEmail, password, 'client', '', '', '');

            if (result && !result.success) {
                throw new Error((result as any).error || "user already exists");
            }

            if (result && result.success && result.message) {
                setSuccessMessage(result.message);
            }
        } catch (err: any) {
            const errorMsg = (err?.message || String(err)).toLowerCase();
            if (errorMsg.includes('abort') || errorMsg.includes('cancelled')) {
                setIsLoading(false);
                return;
            }

            if (errorMsg.includes('wrong_role_register:restaurateur')) {
                setError("Cette adresse email est déjà enregistrée pour un profil Restaurant. Souhaitez-vous rejoindre l'espace Pro ?");
                setMismatchRole('restaurateur');
                setShowRoleMismatchModal(true);
            } else if (errorMsg.includes('wrong_role_register:livreur')) {
                setError("Cette adresse email est déjà enregistrée pour un profil Livreur. Souhaitez-vous rejoindre l'espace Pro ?");
                setMismatchRole('livreur');
                setShowRoleMismatchModal(true);
            } else if (errorMsg.includes("invalid") || errorMsg.includes("validation")) {
                setError(`Adresse email "${cleanEmail}" invalide.`);
                setShowErrorModal(true);
            } else if (errorMsg.includes("rate limit")) {
                setError("Trop de tentatives. Veuillez patienter avant de réessayer.");
                setShowErrorModal(true);
                setCooldown(60);
            } else if (errorMsg.includes("already registered") || errorMsg.includes("user already exists") || errorMsg.includes("déjà")) {
                setShowExistingUserPopup(true);
            } else {
                setError(err.message || "Erreur d'inscription. Veuillez vérifier vos informations.");
                setShowErrorModal(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleAuth = async () => {
        setIsLoading(true);
        setError('');
        try {
            await loginWithGoogle('client', activeTab);
            setView({ screen: 'home' });
        } catch (err: any) {
            console.error("Google Auth Error:", err);
            const errorMsg = String(err.message || "").toLowerCase();
            
            if (errorMsg.includes('not_registered')) {
                setError("Ce compte n'est pas encore inscrit. Veuillez d'abord vous inscrire.");
                setShowErrorModal(true);
            } else if (errorMsg.includes('wrong_role_login:restaurateur') || errorMsg.includes('wrong_role_register:restaurateur')) {
                setError("Ce compte Google appartient à un profil Restaurant. Souhaitez-vous rejoindre l'espace Pro ?");
                setMismatchRole('restaurateur');
                setShowRoleMismatchModal(true);
            } else if (errorMsg.includes('wrong_role_login:livreur') || errorMsg.includes('wrong_role_register:livreur')) {
                setError("Ce compte Google appartient à un profil Livreur. Souhaitez-vous rejoindre l'espace Pro ?");
                setMismatchRole('livreur');
                setShowRoleMismatchModal(true);
            } else {
                setError("Impossible de se connecter avec Google.");
                setShowErrorModal(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // --- Render Modals ---
    const renderSuccessPopup = () => {
        if (!successMessage) return null;
        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
                <div className="bg-white w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden animate-pop-in p-8 text-center">
                    <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                        <span className="text-3xl">📧</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Vérifiez vos emails</h2>
                    <p className="text-gray-600 mb-8 font-medium italic">
                        "Une petite validation par mail est requise, consultez votre boite mail et n'hésitez pas à voir dans les Spams."
                    </p>
                    <button
                        onClick={() => { setSuccessMessage(''); setActiveTab('login'); }}
                        className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-extrabold rounded-2xl shadow-lg shadow-blue-200 transition-all active:scale-[0.98] text-lg"
                    >
                        J'ai compris
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col h-[100dvh] bg-gray-50 relative overflow-y-auto overflow-x-hidden pt-safe-top pb-safe-bottom">
            {/* Top Header */}
            <div className="pt-8 px-6 pb-4">
                <button onClick={onBack} className="w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center text-gray-800 hover:bg-gray-100 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
            </div>

            <div className="flex-1 flex flex-col px-6">
                <div className="text-center mb-6">
                    <div className="h-14 flex justify-center mb-4 mt-2">
                        <img src="/logo-red.svg" alt="Yumii" className="h-full w-auto object-contain" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-1">Espace Client</h1>
                    <p className="text-gray-500 text-sm">Votre aventure gourmande commence ici</p>
                </div>

                {/* Custom Tabs */}
                <div className="bg-gray-200 p-1 rounded-2xl flex mb-8">
                    <button
                        onClick={() => handleTabSwitch('login')}
                        className={`flex-1 py-3 text-sm font-bold rounded-xl transition-all ${activeTab === 'login' ? 'bg-white text-red-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                    >
                        Se Connecter
                    </button>
                    <button
                        onClick={() => handleTabSwitch('register')}
                        className={`flex-1 py-3 text-sm font-bold rounded-xl transition-all ${activeTab === 'register' ? 'bg-white text-red-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                    >
                        S'inscrire
                    </button>
                </div>

                {/* Form Container */}
                <div className="bg-white p-6 rounded-3xl shadow-xl flex-1 mb-[env(safe-area-inset-bottom)] animate-fade-in-up">
                    <form onSubmit={activeTab === 'login' ? handleLogin : handleRegister} className="space-y-4">

                        {activeTab === 'register' && (
                            <div className="animate-fade-in">
                                <label className="text-sm font-semibold text-gray-700">Nom complet</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => { setName(e.target.value); setError(''); }}
                                    placeholder="Votre nom"
                                    required
                                    className="w-full p-4 mt-1 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white transition-all text-gray-900"
                                />
                            </div>
                        )}

                        <div>
                            <label className="text-sm font-semibold text-gray-700">Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                                placeholder="email@example.com"
                                required
                                className="w-full p-4 mt-1 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white transition-all text-gray-900"
                            />
                        </div>

                        <div>
                            <div className="flex justify-between items-center">
                                <label className="text-sm font-semibold text-gray-700">Mot de passe</label>
                                {activeTab === 'login' && (
                                    <button type="button" onClick={onForgotPassword} className="text-xs text-red-600 font-bold hover:underline">Oublié ?</button>
                                )}
                            </div>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                                placeholder="••••••••"
                                required
                                className="w-full p-4 mt-1 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white transition-all text-gray-900"
                            />
                        </div>

                        {cooldown > 0 && activeTab === 'register' && (
                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700 text-center">
                                Veuillez patienter {cooldown}s.
                            </div>
                        )}

                        <div className="pt-2">
                            <button
                                type="submit"
                                disabled={isLoading || (activeTab === 'register' && cooldown > 0)}
                                className="w-full py-4 font-bold text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors shadow-lg shadow-red-200 disabled:bg-gray-400 disabled:shadow-none active:scale-[0.98]"
                            >
                                {isLoading ? 'Patientez...' : (activeTab === 'login' ? 'Se Connecter' : "S'inscrire")}
                            </button>
                        </div>
                    </form>

                    <div className="relative flex items-center py-6">
                        <div className="flex-grow border-t border-gray-200"></div>
                        <span className="flex-shrink mx-4 text-gray-400 text-sm font-medium">OU</span>
                        <div className="flex-grow border-t border-gray-200"></div>
                    </div>

                    <button
                        onClick={handleGoogleAuth}
                        disabled={isLoading}
                        className="w-full py-4 flex items-center justify-center gap-3 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all disabled:bg-gray-100 disabled:opacity-50 active:scale-[0.98]"
                    >
                        <GoogleIcon className="w-6 h-6" />
                        <span className="font-bold text-gray-700">Continuer avec Google</span>
                    </button>

                    {import.meta.env.DEV && (
                        <div className="mt-8 p-4 bg-gray-50 rounded-2xl border border-gray-200 border-dashed">
                            <p className="text-[9px] font-black text-gray-400 uppercase mb-3 text-center tracking-widest">🧪 Mode Local (Auto-Login)</p>
                            <div className="flex gap-2">
                                <button 
                                    type="button"
                                    onClick={() => devCreateTestAccount?.(`admin_${Date.now()}@yumii.dz`, 'Admin Local', 'super_admin')}
                                    className="flex-1 py-2 bg-red-600 text-white text-[9px] font-black rounded-xl shadow-lg active:scale-95 transition-all uppercase tracking-tight"
                                >
                                    ✨ Admin
                                </button>
                                <button 
                                    type="button"
                                    onClick={() => devCreateTestAccount?.(`resto_${Date.now()}@yumii.dz`, 'Resto Local', 'restaurateur')}
                                    className="flex-1 py-2 bg-gray-900 text-white text-[9px] font-black rounded-xl shadow-lg active:scale-95 transition-all uppercase tracking-tight"
                                >
                                    🥗 Resto
                                </button>
                                <button 
                                    type="button"
                                    onClick={() => devCreateTestAccount?.(`client_${Date.now()}@yumii.dz`, 'Client Local', 'client')}
                                    className="flex-1 py-2 bg-blue-600 text-white text-[9px] font-black rounded-xl shadow-lg active:scale-95 transition-all uppercase tracking-tight"
                                >
                                    😋 Client
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* -- Modals (Re-used from old screens) -- */}
            {renderSuccessPopup()}

            {showErrorModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="bg-white rounded-3xl shadow-2xl p-6 w-full max-w-sm text-center border border-gray-100">
                        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-extrabold text-gray-900 mb-2">Attention</h3>
                        <p className="text-gray-600 mb-8 px-2 font-medium leading-relaxed">{error}</p>
                        <button
                            onClick={() => setShowErrorModal(false)}
                            className="w-full py-3.5 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-colors shadow-lg active:scale-95 duration-150"
                        >
                            Fermer
                        </button>
                    </div>
                </div>
            )}

            {showRoleMismatchModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-md p-4 animate-fade-in">
                    <div className="bg-white rounded-[2.5rem] shadow-2xl p-8 w-full max-w-sm text-center border border-white/20 animate-pop-in">
                        <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-red-50 mb-6">
                            <span className="text-4xl">🏢</span>
                        </div>
                        <h3 className="text-2xl font-black text-gray-900 mb-3 tracking-tight">Compte Pro détecté</h3>
                        <p className="text-gray-600 mb-8 px-2 font-medium leading-relaxed">
                            {error}
                        </p>
                        <div className="space-y-3">
                            <button
                                onClick={() => {
                                    setShowRoleMismatchModal(false);
                                    onSwitchToPro();
                                }}
                                className="w-full py-4 bg-red-600 text-white font-black rounded-2xl hover:bg-red-700 transition-all shadow-xl shadow-red-200 active:scale-95"
                            >
                                Aller à l'Espace Pro
                            </button>
                            <button
                                onClick={() => setShowRoleMismatchModal(false)}
                                className="w-full py-3 text-gray-400 font-bold hover:text-gray-600 transition-colors"
                            >
                                Annuler
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showIncorrectPasswordPopup && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-scale-up">
                        <div className="p-6 text-center">
                            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Oups, ce n'est pas le bon mot de passe</h3>
                            <p className="text-sm text-gray-600 mb-6">Le mot de passe saisi pour l'adresse <strong>{email}</strong> est incorrect.</p>
                            <div className="space-y-3">
                                <button onClick={() => setShowIncorrectPasswordPopup(false)} className="w-full py-3 font-bold text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors shadow-sm">Réessayer</button>
                                <button
                                    onClick={() => { setShowIncorrectPasswordPopup(false); onForgotPassword(); }}
                                    className="w-full py-3 font-bold text-red-600 border-2 border-red-100 rounded-xl hover:bg-red-50 transition-colors"
                                >Mot de passe oublié ?</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showExistingUserPopup && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl animate-scale-up">
                        <div className="p-8 text-center">
                            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                                <span className="text-2xl">👋</span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Vous êtes déjà membre</h3>
                            <p className="text-sm text-gray-600 mb-8">Un compte avec l'adresse <strong>{email}</strong> existe déjà sur Yumii.</p>
                            <div className="space-y-3">
                                <button onClick={() => { setShowExistingUserPopup(false); setActiveTab('login'); }} className="w-full py-4 font-bold text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors">Me connecter</button>
                                <button onClick={() => setShowExistingUserPopup(false)} className="w-full py-3 text-sm font-bold text-gray-500 hover:text-gray-800 transition-colors">Annuler</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default ClientAuthScreen;
